import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { firstAccessibleRoute, hasNavAccess } from '../layouts/navConfig';
import { ILMSButton } from '../components/ILMSButton';
import { ILMSAlert } from '../components/ILMSAlert';
import { ILMSInput } from '../components/ILMSForm';
import { ILMSModal } from '../components/ILMSModal';
import { logoUrl, useSite } from '../utils/site';

export default function Login() {
  const site = useSite();
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError('Please enter your username and password.');
      return;
    }
    try {
      const { roles, permissions } = await login(username.trim(), password);
      // The usual default (/dashboard, or wherever they were headed before
      // being bounced to login) might not be one of this user's assigned
      // pages — Page Access restrictions no longer guarantee Dashboard (see
      // ILMSPageAccessPicker.tsx). Land them on their first actually-
      // accessible page instead of a "You do not have permission" screen.
      const destination = hasNavAccess(from, roles, permissions) ? from : firstAccessibleRoute(roles, permissions);
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex flex-col items-center lg:items-start mb-6">
        <img src={logoUrl(site)} alt="Sri KalpaGavya" className="h-16 w-auto object-contain lg:hidden mb-3" />
        <h1 className="text-page-title text-center lg:text-left">Sign in to ILMS</h1>
        <p className="text-secondary text-ink-500 mt-1 text-center lg:text-left">
          Enter your username and password to continue.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}

        <div className="flex flex-col gap-1">
          <label htmlFor="username" className="text-label text-ink-700">
            Username
          </label>
          <ILMSInput
            id="username"
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. jsmith"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="password" className="text-label text-ink-700">
            Password
          </label>
          <div className="relative">
            <ILMSInput
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center text-ink-400 hover:text-ink-700"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="flex justify-end -mt-1">
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            className="text-secondary text-brand-green-700 hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <ILMSButton type="submit" size="lg" fullWidth loading={isLoading} icon={<LogIn />}>
          Sign in
        </ILMSButton>
      </form>

      <p className="text-helper-text text-ink-400 text-center mt-6">
        Sri KalpaGavya — Nurturing Life, Harvesting Prosperity.
      </p>

      <ILMSModal open={forgotOpen} onClose={() => setForgotOpen(false)} title="Forgot your password?" size="sm"
        footer={<ILMSButton onClick={() => setForgotOpen(false)}>Got it</ILMSButton>}
      >
        <p className="text-body text-ink-700">
          Please contact your farm administrator to reset your password. Self-service password reset
          will be available in a future update.
        </p>
      </ILMSModal>
    </div>
  );
}
